<template>
  <div class="container">
    <!-- Upload form -->
    <form
      v-if="canAddFiles"
      enctype="multipart/form-data"
      novalidate
    >
      <h3
        v-if="notEmbedded"
        class="text-h6"
      >
        Upload Files
      </h3>
      <v-card
        outlined
        ripple
        hover
        height="200"
        class="d-flex my-2"
      >
        <input
          ref="fileInput"
          class="input-file"
          type="file"
          :accept="acceptedExtensions"
          :disabled="isSaving"
          :multiple="multiple"
          :name="uploadFieldName"
          @change="updateFiles"
        >
        <p class="ma-auto text-center">
          <span class="text-h6 d-block font-weight-regular">Drag files to upload</span> or <br>
          <span
            class="upload-btn ma-2 d-inline-block"
          >
            Browse Files
          </span>
          <span class="d-block text-caption text--disabled mt-4">Main supported file types: <span class="font-weight-bold">PDF, CSV, JPG, PNG, MP3, MP4, MOV, M4A</span></span>
          <span class="d-block text-caption text--disabled">Max batch size: <span class="font-weight-bold">50MB</span></span>
        </p>
      </v-card>
    </form>

    <v-data-table
      v-if="selectedFiles.length > 0"
      :headers="headers"
      :items="selectedFiles"
      :items-per-page="30"
      class="elevation-1 upload"
      hide-default-footer
    >
      <template #[`header.name`]="{ header }">
        {{ header.text }}

        <v-menu
          v-model="showFileHelp"
          offset-y
        >
          <template #activator="{ on }">
            <v-btn
              text
              icon
              title="File Name help"
              v-on="on"
              @click.native.stop
            >
              <v-icon>help_outline</v-icon>
            </v-btn>
          </template>

          <v-card max-width="300">
            <v-card-text>
              <h4>File Name Help</h4>
              <p>Files can be renamed here by clicking on the edit icon, editing the name, and clicking the save icon.</p>
              <p>
                {{ isForDocketEntries ? ' If you want to rename a file that is part of a Document Set used by other Docket Entries, the file must be renamed from the Settings page.'
                  : 'Renaming a file in a Document Set will rename the file in all Docket Entries that use that Set.' }}
              </p>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                @click="showFileHelp = false"
              >
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </template>

      <template #[`item.preview`]="{ item }">
        <img
          v-if="item.representable"
          class="img-thumb"
          :src="item.preview"
        >
        <v-icon v-else>
          attachment
        </v-icon>
      </template>

      <template #[`item.name`]="{ item }">
        <div
          v-if="!item.editActive"
          :title="item.name.length > MAX_FILE_NAME_LEN ? item.name : ''"
        >
          {{ truncateFileName(item.name) }}
          <v-btn
            icon
            small
            title="Rename file"
            :disabled="fileIsNotRenameable(item)"
            @click="() => activateFileNameEdit(item)"
          >
            <v-icon>edit</v-icon>
          </v-btn>
        </div>

        <template v-else-if="item.editActive">
          <v-form
            v-model="fileNameValid[selectedFiles.findIndex(f => f.id === item.id)]"
            method="post"
            autocomplete="off"
            @submit.prevent="() => updateFileName(item)"
          >
            <v-text-field
              v-model="item.rename"
              filled
              dense
              label="Rename file"
              :rules="[rules.required, rules.fileName]"
            >
              <template #append>
                <v-btn
                  icon
                  title="Save file name"
                  @click="() => updateFileName(item)"
                >
                  <v-icon>save</v-icon>
                </v-btn>
              </template>

              <template #prepend>
                <v-btn
                  icon
                  title="Cancel"
                  @click="() => cancelFileRename(item)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-form>
        </template>
      </template>

      <template #[`item.progress`]="{ item }">
        <div
          v-if="item.active || item.progress !== '0.00'"
          class="progress"
        >
          <div
            class="progress-bar progress-bar-striped"
            :class="{'bg-danger': item.error, 'progress-bar-animated': item.active}"
            role="progressbar"
            :style="{width: item.progress + '%'}"
          >
            {{ item.progress }}%
          </div>
        </div>
      </template>

      <template #[`item.status`]="{item}">
        <v-tooltip
          slot="append"
          bottom
        >
          <template #activator="{ on }">
            <v-icon
              :color="item.uploaded ? 'green' : 'grey'"
              v-on="on"
            >
              {{ item.uploaded ? 'check_circle' : 'upload' }}
            </v-icon>
          </template>
          <span>{{ item.status }}</span>
        </v-tooltip>
      </template>

      <template #[`item.size`]="{ item }">
        {{ formatSize(item.size) }}
      </template>

      <template #[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template #activator="{on, attrs}">
            <v-btn
              dark
              x-small
              fab
              outlined
              v-bind="attrs"
              color="secondary"
              v-on="on"
              @click="() => removeOrDeleteFile(item.id)"
            >
              <v-icon>{{ item.uploaded ? 'delete' : 'clear' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ item.uploaded ? 'Delete' : 'Remove' }} file</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-row>
      <v-col
        v-if="filesToUploadCount > 0"
        cols="5"
        class="offset-sm-7"
      >
        <v-alert
          dense
          outlined
          :type="validUpload ? 'info' : 'error'"
        >
          Total file{{ filesToUploadCount > 1 ? 's' : '' }}: {{ filesToUploadCount }}, {{ formatSize(totalUploadSize) }}
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { extensionMatchesFileType, fileTypesString, formatSize } from '@/utilities'
import { rules } from '@/utilities/validation-rules'

const MAX_FILE_COUNT = 10
const MAX_FILE_NAME_LEN = 20
const REMOVE_ALL_FILES = true
const STATUS_INITIAL = 0
const STATUS_PREPARING = 1
const STATUS_SAVING = 2
const UPLOADED = true
const VALID = true

export default {
  name: 'FileUploader',

  props: {
    editedObjectId: {
      type: Number,
      default: 0,
    },

    existingFiles: {
      type: Array,
      default: () => [],
    },

    filePreviews: {
      type: Array,
      required: true,
    },

    fileType: {
      type: String,
      default: null,
    },

    forDataType: {
      type: String,
      required: true,
    },

    multiple: {
      type: Boolean,
      default: true,
    },

    notEmbedded: {
      type: Boolean,
      default: true,
    },

    saveActivated: {
      type: Boolean,
      required: true,
    },
  },

  data () {
    return {
      MAX_FILE_NAME_LEN: MAX_FILE_NAME_LEN,
      acceptedExtensions: this.fileType || fileTypesString,
      currentStatus: null,
      fileNameValid: [],
      headers: [
        { sortable: false, text: 'Preview', value: 'preview' },
        { sortable: false, text: 'File name', value: 'name' },
        { sortable: false, text: 'Size', value: 'size' },
        { sortable: false, text: 'Status', value: 'status' },
        { sortable: false, text: 'Actions', value: 'actions' },
      ],

      latestId: 0,
      rules: rules,
      selectedFiles: [],
      showFileHelp: false,
      totalUploadSize: 0,
      uploadFieldName: 'files',
    }
  },

  computed: {
    canAddFiles () {
      return this.underMaxUploadSize && this.filesToUploadCount < 10
    },

    filesToUploadCount () {
      const filesToUpload = this.selectedFiles.filter(file => !file.uploaded)
      return filesToUpload ? filesToUpload.length : 0
    },

    hasFiles () {
      return this.existingFiles.length > 0
    },

    isForDocketEntries () {
      return this.forDataType === 'docketEntries'
    },

    isSaving () {
      return this.currentStatus === STATUS_SAVING
    },

    underMaxFileCount () {
      return this.filesToUploadCount <= MAX_FILE_COUNT
    },

    underMaxUploadSize () {
      const MEGABYTES_50 = 52428800
      return this.totalUploadSize < MEGABYTES_50
    },

    validUpload () {
      return this.underMaxFileCount && this.underMaxUploadSize
    },

    ...mapGetters([
      'currentUser',
    ]),
  },

  watch: {
    saveActivated (newValue) {
      if (newValue) {
        this.$emit('on-prepare', this.selectedFiles.filter(file => !file.uploaded))
        this.reset(REMOVE_ALL_FILES)
      }
    },

    validUpload (newValue) {
      let errorMessage = 'Error: '

      if (!newValue) {
        const uploadSizeMsg = 'Total upload size must be at or under 50MB'
        const fileCountMsg = 'Only 10 files can be uploaded at a time'

        if (!this.underMaxUploadSize) errorMessage += `${uploadSizeMsg}. `
        if (!this.underMaxFileCount) errorMessage += ` ${fileCountMsg}`

        this.emitValidUpdateAlert(newValue, 'error', errorMessage)
      } else this.$emit('is-valid-update', { uploadValid: newValue })
    },
  },

  created () {
    // Filter out null/undefined entries
    const files = this.existingFiles.filter(file => file)
    if (files.length === 0) return

    files.forEach(file => {
      this.addToSelectedFiles(file, (file.blob !== undefined))
    })
    this.selectedFiles.sort((a, b) => { return b.id - a.id })
    this.latestId = files[0].id
  },

  methods: {
    activateFileNameEdit (file) {
      file.rename = file.name
      file.editActive = true

      // prevent entry save while file name edit is active
      this.$emit('is-valid-update', { uploadValid: false })
    },

    addToSelectedFiles (fileBlob, isAlreadyUploaded) {
      // files that have been uploaded  and are sent from back-end
      // will have a different obj structure
      const selectedFile = {
        data: isAlreadyUploaded ? null : fileBlob,
        editActive: false,
        id: isAlreadyUploaded ? fileBlob.id : ++this.latestId,
        name: isAlreadyUploaded ? fileBlob.blob.filename : fileBlob.name,
        preview: isAlreadyUploaded ? fileBlob.preview : '',
        rename: null,
        renameable: isAlreadyUploaded ? fileBlob['renameable?'] : true,
        representable: isAlreadyUploaded ? fileBlob['representable?'] : fileBlob.type.includes('image/'),
        size: isAlreadyUploaded ? fileBlob.blob.byte_size : fileBlob.size,
        status: isAlreadyUploaded ? 'Attached' : 'Pending',
        type: isAlreadyUploaded ? fileBlob.blob.content_type : fileBlob.type,
        uploaded: isAlreadyUploaded,
      }

      if (!isAlreadyUploaded) {
        this.updateTotalUploadSize(selectedFile.size, true)
        if (selectedFile.representable) this.createImage(fileBlob, selectedFile)
      } else if (isAlreadyUploaded && fileBlob['representable?']) this.loadThumbnailFiles(selectedFile)

      if (this.multiple) this.selectedFiles.push(selectedFile)
      else this.selectedFiles = [selectedFile]
    },

    cancelFileRename (file) {
      file.editActive = false

      this.$emit('is-valid-update', { uploadValid: true })
    },

    createImage (blob, selectedFile) {
      if (selectedFile.preview) return

      const fileReader = new FileReader()

      if (blob) fileReader.readAsDataURL(blob)
      fileReader.onloadend = () => {
        selectedFile.preview = fileReader.result
      }
    },

    emitValidUpdateAlert (isValid, alertType, alertMessage) {
      this.$emit('is-valid-update', {
        alertMessage: alertMessage,
        alertType: alertType,
        uploadValid: isValid,
      })
    },

    fileHasDuplicateName (fileName) {
      let fileHasDuplicateName = false
      // using for instead of forEach since we
      // need to 'break' once a match is found
      const totalFiles = this.selectedFiles.length

      if (totalFiles > 0) {
        for (let i = 0; i < totalFiles; i++) {
          if (this.selectedFiles[i].name === fileName) {
            fileHasDuplicateName = true
            break
          }
        }
      }

      return fileHasDuplicateName
    },

    fileIsNotRenameable (file) {
      return this.isForDocketEntries ? !file.renameable : false
    },

    formatSize: formatSize,

    loadThumbnailFiles (selectedFile) {
      const loadedFileWithPreview = this.filePreviews.find(f => f.id === selectedFile.id)
      const vm = this

      if (typeof loadedFileWithPreview === 'undefined') {
        this.getThumbnailForAttachment({ fileId: selectedFile.id }).then(blob => {
          vm.createImage(blob, selectedFile)
          vm.$emit('preview-fetched', { fileId: selectedFile.id, preview: blob })
        })
      } else this.createImage(loadedFileWithPreview.preview, selectedFile)
    },

    onPickFile () {
      this.$refs.fileInput.click()
    },

    removeOrDeleteFile (fileId) {
      const fileIndex = this.selectedFiles.findIndex(file => file.id === fileId)
      if (fileIndex === -1) return

      const uploadedFiles = this.selectedFiles.filter(file => file.uploaded)

      if (this.selectedFiles[fileIndex].uploaded && this.currentUser.atleastEditor) {
        if (uploadedFiles.length === 1 && this.isForDocumentSets) {
          this.emitValidUpdateAlert(VALID, 'error',
            'Document Set must have at least one file, please upload another file before deleting')
          return
        }

        // display confirm window
        const confirmDelete = window.confirm('Are you sure you want to delete this file? This cannot be undone')
        if (confirmDelete) {
          const requestData = {
            dataId: this.editedObjectId,
            fileId: this.selectedFiles[fileIndex].id,
            forDataType: this.forDataType,
          }
          const vm = this

          this.destroyAttachmentModel(requestData).
            then(() => {
              this.emitValidUpdateAlert(VALID, 'success', 'Successfully removed file')
              vm.selectedFiles.splice(fileIndex, 1)
            }).
            catch(() => {
              this.emitValidUpdateAlert(!VALID, 'error', 'Could not remove file, please try again.')
            })
        }
      } else if (!this.selectedFiles[fileIndex].uploaded) {
        this.updateTotalUploadSize(this.selectedFiles[fileIndex].size, false)
        this.selectedFiles.splice(fileIndex, 1)
        this.$emit('files-updated', this.filesToUploadCount)
      } else if (!this.currentUser.atleastEditor) this.emitValidUpdateAlert(!VALID, 'error', 'Your account does not have the permissions to delete files')
    },

    reset (removeAllFiles) {
      // reset form to initial state
      this.currentStatus = STATUS_INITIAL

      if (removeAllFiles) this.selectedFiles = []
      else {
        // only remove non-uploaded files from selectedFiles
        const temp = [...this.selectedFiles]

        for (let i = 0; i < temp.length; i++) if (!temp[i].uploaded && this.selectedFiles.indexOf(temp[i].id) !== -1) this.selectedFiles.splice(i, 1)
      }
    },

    truncateFileName (fileName) {
      if (fileName.length > MAX_FILE_NAME_LEN) return `${fileName.slice(0, MAX_FILE_NAME_LEN).trimEnd()}...`
      else return fileName
    },

    updateFileName (file) {
      const fileIndex = this.selectedFiles.findIndex(f => f.id === file.id)
      if (fileIndex === -1 || !this.fileNameValid[fileIndex]) return

      const fileOrigName = file.name
      const fileParts = file.name.split('.')
      const fileOrigExt = fileParts.length > 1 ? fileParts[fileParts.length - 1] : null

      // checking for extension match if one was added
      if (file.rename.includes('.')) {
        const fileRenameParts = file.rename.split('.')
        const fileRenameExt = fileRenameParts[fileRenameParts.length - 1]

        if (fileRenameExt !== fileOrigExt) {
          // check for validity if different extension from original
          const fileType = file.blob ? file.blob.content_type : file.type

          if (extensionMatchesFileType(fileRenameExt, fileType)) file.name = file.rename
          else {
            this.emitValidUpdateAlert(!VALID, 'error', 'File extension does not match file type, please try again')
            return
          }
        } else file.name = file.rename

      // handling edge case where there might not have been an
      // extension in original file name
      } else file.name = fileOrigExt ? `${file.rename}.${fileOrigExt}` : file.rename

      file.editActive = false
      if (!file.uploaded) this.$emit('is-valid-update', { uploadValid: true })
      else {
        const renameParams = {
          dataId: this.editedObjectId,
          fileId: file.id,
          fileName: file.name,
          forDocketEntry: this.isForDocketEntries,
        }

        this.updateAttachmentFileName(renameParams).
          then(() => {
            this.emitValidUpdateAlert(VALID, 'success', 'File was succesfully renamed')
          }).
          catch(() => {
            file.name = fileOrigName
            this.emitValidUpdateAlert(VALID, 'error', 'Could not rename uploaded file, please try again')
          })
      }
    },

    updateFiles (event) {
      const files = event.target.files || event.dataTransfer.files
      if (!files.length) return

      this.currentStatus = STATUS_PREPARING
      const filesLength = Object.keys(files).length

      for (const key in files) {
        if (files[key] && typeof files[key] === 'object') {
          // handle special case of 'image', as it can't be compared using 'acceptedExtensions' string
          const wrongFileType = !(this.acceptedExtensions.includes(files[key].type) || files[key].type.includes('image/'))

          if (wrongFileType && (filesLength > 1 || this.selectedFiles.length > 0)) {
            this.emitValidUpdateAlert(VALID, 'error', `File type ${files[key].type} not accepted`)
            continue
          } else if (wrongFileType && filesLength === 1 && this.selectedFiles.length === 0) {
            this.emitValidUpdateAlert(VALID, 'error', `File type ${files[key].type} not accepted`)
            this.reset(!REMOVE_ALL_FILES)
            return
          } else if (this.fileHasDuplicateName(files[key].name)) {
            const dataType = this.isForDocketEntries ? 'docket entry' : 'entry'
            this.emitValidUpdateAlert(VALID, 'error',
                                      `This ${dataType} already has a file of name "${files[key].name}", 
                please add a file with a different name`)
            continue
          }

          this.addToSelectedFiles(files[key], !UPLOADED)
        }
      }

      this.$emit('files-updated', this.filesToUploadCount)
      // resetting input field
      // to be able to re-add files
      event.target.value = ''
    },

    updateTotalUploadSize (newFileSize, added) {
      if (added) this.totalUploadSize += newFileSize
      else this.totalUploadSize -= newFileSize
    },

    ...mapActions([
      'destroyAttachmentModel',
      'getThumbnailForAttachment',
      'updateAttachmentFileName',
    ]),
  },
}
</script>

<style lang="scss" scoped>
.input-file {
  opacity: 0;
  width: 100%;
  height: 200px;
  position: absolute;
  cursor: pointer;
}
.img-thumb {
  max-width: 120px;
}

.upload-btn {
  border: solid var(--v-secondary-base) 1px;
  padding: 5px 15px;
  border-radius: 20px;
}
</style>
